/**
 * Custom hook for device import logic.
 * Manages file upload, status polling, and state transitions for device CSV import.
 *
 * Flow: File Selection → Validation → Upload → Poll Status → Display Result
 *
 * @param {Object} params
 * @param {Function} [params.onImportComplete] - Callback triggered after successful import
 * @param {Function} params.setOpen - Function to close the dialog
 * @returns {Object} State, refs, handlers, and computed values for the import workflow
 * @returns {Object} state - Current state (file, phase, importStatus, importMessage, importErrors, clientError, uploadFailed, uploadFailMessage)
 * @returns {Object} refs - React refs (fileInputRef, importCompletedRef, importIdRef)
 * @returns {Object} handlers - Event handlers (resetState, handleFileSelect, handleUpload, handleClose)
 * @returns {Object} computed - Computed values (isSelectPhase, isUploadingPhase, isResultPhase, uploadDisabled, errorMessage, statusMessageClass)
 */
import { useCallback, useRef, useState } from 'react';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';

import { useDataService } from '../../../../services/ApiDataService';
import usePollingApi from './UsePollingApi';
import {
    ERROR_TYPES,
    IMPORT_CSV_URL,
    IMPORT_STATUS_URL,
    MAX_FILE_SIZE,
    POLL_INTERVAL_MS,
    STATUS_DISPLAY_MAP,
    STATUS_FAILED,
    STATUS_PARTIAL_SUCCESS,
    STATUS_PROCESSING,
    STATUS_SUCCESS,
} from '../device/DeviceImportDialog.constants';

export function useDeviceImport({ onImportComplete, setOpen }) {
    const dataService = useDataService();

    // State management
    const [file, setFile] = useState(null);
    const [phase, setPhase] = useState('select'); // 'select' | 'uploading' | 'result'
    const [importStatus, setImportStatus] = useState(null);
    const [importMessage, setImportMessage] = useState('');
    const [importErrors, setImportErrors] = useState([]);
    const [clientError, setClientError] = useState(null); // 'fileSize' | 'fileExistence' | null
    const [uploadFailed, setUploadFailed] = useState(false);
    const [uploadFailMessage, setUploadFailMessage] = useState('');

    // Refs
    const fileInputRef = useRef(null);
    const importCompletedRef = useRef(false);
    const importIdRef = useRef(null);

    // Polling hook for import status
    const polling = usePollingApi({
        pollFn: async () => {
            const data = await dataService.get(IMPORT_STATUS_URL, { id: importIdRef.current });
            return data;
        },
        onPoll: (response) => {
            const { status, message, errorLog } = response;

            if (status === STATUS_PROCESSING) {
                setImportMessage(message || i18n._(t`Import in progress`));
                return true; // Continue polling
            }

            // Import completed — stop polling
            const displayStatus = STATUS_DISPLAY_MAP[status] || status;
            setImportStatus(status);
            setImportMessage(`${displayStatus}: ${message}`);
            setPhase('result');
            importCompletedRef.current = true;

            if ((status === STATUS_PARTIAL_SUCCESS || status === STATUS_FAILED) && isArray(errorLog)) {
                setImportErrors(errorLog);
            }

            return false; // Stop polling
        },
        interval: POLL_INTERVAL_MS,
        useSetInterval: true,
        onError: () => {
            setImportStatus(STATUS_FAILED);
            setImportMessage(i18n._(t`Failed to check import status`));
            setPhase('result');
        },
    });

    /**
     * Resets all state to initial values.
     */
    const resetState = useCallback(() => {
        setFile(null);
        setPhase('select');
        setImportStatus(null);
        setImportMessage('');
        setImportErrors([]);
        setClientError(null);
        setUploadFailed(false);
        setUploadFailMessage('');
        importCompletedRef.current = false;
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    /**
     * Handles file selection from input.
     *
     * @param {Event} event - File input change event
     */
    const handleFileSelect = useCallback((event) => {
        setClientError(null);
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) return;
        setFile(selectedFile);
    }, []);

    /**
     * Uploads the selected file and starts status polling.
     * Validates file existence and size before upload.
     */
    const handleUpload = useCallback(async () => {
        // Client-side validation
        if (!file) {
            setClientError(ERROR_TYPES.FILE_EXISTENCE);
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setClientError(ERROR_TYPES.FILE_SIZE);
            return;
        }

        setClientError(null);
        setPhase('uploading');
        setImportMessage(i18n._(t`Uploading file...`));

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await dataService.postFormData(IMPORT_CSV_URL, formData);

            if (!response || response.status === STATUS_FAILED) {
                setUploadFailed(true);
                setUploadFailMessage(response?.message || i18n._(t`Upload failed`));
                setPhase('result');
                setImportStatus(STATUS_FAILED);
                setImportMessage(
                    `${STATUS_DISPLAY_MAP[STATUS_FAILED]}: ${response?.message || i18n._(t`Upload failed`)}`
                );
                return;
            }

            // Start polling for import status
            importIdRef.current = response.id;
            setImportMessage(i18n._(t`Import in progress`));
            polling.start();
        } catch (error) {
            setUploadFailed(true);
            setUploadFailMessage(i18n._(t`Failed to upload file`));
            setPhase('result');
            setImportStatus(STATUS_FAILED);
            setImportMessage(`${STATUS_DISPLAY_MAP[STATUS_FAILED]}: ${i18n._(t`Failed to upload file`)}`);
        }
    }, [file, dataService, polling]);

    /**
     * Closes the dialog, stops polling, and triggers refresh callback if import completed.
     */
    const handleClose = useCallback(() => {
        // Stop any active polling
        polling.stop();

        // Trigger list refresh if import was completed
        if (importCompletedRef.current && isFunction(onImportComplete)) {
            onImportComplete();
        }

        resetState();
        setOpen(false);
    }, [polling, onImportComplete, resetState, setOpen]);

    /**
     * Returns CSS class name for import status message.
     *
     * @param {string} status - Import status
     * @param {Object} classes - Material-UI style classes
     * @returns {string} CSS class name
     */
    const getStatusMessageClass = useCallback((status, classes) => {
        if (status === STATUS_SUCCESS) return classes.successMessage;
        if (status === STATUS_PARTIAL_SUCCESS) return classes.partialMessage;
        if (status === STATUS_FAILED) return classes.failedMessage;
        return '';
    }, []);

    // Computed values
    const isSelectPhase = phase === 'select';
    const isUploadingPhase = phase === 'uploading';
    const isResultPhase = phase === 'result';
    const uploadDisabled = !file || isUploadingPhase;

    const errorMessage = (() => {
        if (clientError === ERROR_TYPES.FILE_SIZE) {
            return i18n._(t`Import failed. Chosen file exceeded maximum file size of 20 MB.`);
        }
        if (clientError === ERROR_TYPES.FILE_EXISTENCE) {
            return i18n._(t`Import failed. File does not exist.`);
        }
        return null;
    })();

    return {
        state: {
            file,
            phase,
            importStatus,
            importMessage,
            importErrors,
            clientError,
            uploadFailed,
            uploadFailMessage,
        },
        refs: {
            fileInputRef,
            importCompletedRef,
            importIdRef,
        },
        handlers: {
            resetState,
            handleFileSelect,
            handleUpload,
            handleClose,
            getStatusMessageClass,
        },
        computed: {
            isSelectPhase,
            isUploadingPhase,
            isResultPhase,
            uploadDisabled,
            errorMessage,
        },
    };
}
